import React from 'react';
import '../../config/style.css';
import { Grid, Typography, Box, Accordion, AccordionSummary, AccordionDetails, Paper, Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MaterialUITheme from '../../config/material-ui-theme';
import HomeHeroFlowVisual from './HomeHeroFlowVisual';
import useScript from '../../config/useScript';
import { useAppContext } from '../../context';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import PlayArrowOutlined from '@material-ui/icons/PlayArrowOutlined';
import HomeSEO from './SEO';
import { NavLink } from 'react-router-dom';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import StorefrontOutlined from '@material-ui/icons/StorefrontOutlined';
import TrendingUpOutlined from '@material-ui/icons/TrendingUpOutlined';
import TableChartOutlined from '@material-ui/icons/TableChartOutlined';
import AssessmentOutlined from '@material-ui/icons/AssessmentOutlined';
import DeviceHubOutlined from '@material-ui/icons/DeviceHubOutlined';
import BusinessOutlined from '@material-ui/icons/BusinessOutlined';
import GroupOutlined from '@material-ui/icons/GroupOutlined';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import TuneOutlined from '@material-ui/icons/TuneOutlined';
import AutorenewOutlined from '@material-ui/icons/AutorenewOutlined';
import GridOnIcon from '@material-ui/icons/GridOn';
import { preserveTrackingParams } from '../../utils/hubspotTracking';
import { SHOPIFY_QB_LANDING } from '../../data/shopifyQuickBooksLanding';
import { PRICING_LANDING } from '../../data/pricingLanding';
import ScrollRevealSection from './ScrollRevealSection';
import HomeInteractiveSetup from './HomeInteractiveSetup';
import HomeDashboardShowcase from './HomeDashboardShowcase';

/** Homepage-only styles: hover lifts, FAQ polish */
const homeScopedCss = `
.dc-home-main {
  max-width: 100%;
}
@supports (overflow-x: clip) {
  .dc-home-main {
    overflow-x: clip;
  }
}
/* Persona pick row: explicit grid + minmax(0) so columns never overlap (MUI Grid/quirks-safe) */
.home-persona-cards {
  display: grid;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
}
@media (min-width: 960px) {
  .home-persona-cards {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
  }
}
.home-persona-cards > a {
  display: block !important;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
.home-persona-cards .home-automate-card {
  box-sizing: border-box;
  max-width: 100%;
}
.home-persona-infographic .home-persona-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #64748b;
  text-align: center;
  margin: 0 auto 22px;
}
.home-persona-flow {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px 6px;
  margin: 0 0 6px;
  width: 100%;
}
.home-persona-node {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 11px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.25;
  text-transform: uppercase;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(15, 23, 42, 0.55);
  color: #e2e8f0;
}
.home-persona-node--orange {
  border-color: rgba(251, 146, 60, 0.4);
  background: rgba(251, 146, 60, 0.1);
  color: #fed7aa;
}
.home-persona-node--sky {
  border-color: rgba(56, 189, 248, 0.4);
  background: rgba(56, 189, 248, 0.1);
  color: #bae6fd;
}
.home-persona-flow-chevron {
  font-size: 18px !important;
  color: #475569 !important;
  opacity: 0.85;
  flex-shrink: 0;
}
@keyframes home-how-flow-pulse {
  0%, 100% {
    filter: brightness(1);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.07),
      0 0 20px rgba(56, 189, 248, 0.28),
      0 0 44px rgba(56, 189, 248, 0.14);
  }
  50% {
    filter: brightness(1.1);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.1),
      0 0 32px rgba(56, 189, 248, 0.48),
      0 0 64px rgba(56, 189, 248, 0.22);
  }
}
.home-how-flow-chip--pulse {
  animation: home-how-flow-pulse 2.4s ease-in-out infinite;
  z-index: 1;
}
.home-trust-micro-cta {
  color: #38bdf8;
  transition: color 0.15s ease;
}
.home-trust-micro-cta:hover {
  color: #7dd3fc;
}
@media (prefers-reduced-motion: reduce) {
  .home-how-flow-chip--pulse {
    animation: none !important;
    filter: none !important;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.08),
      0 0 28px rgba(56, 189, 248, 0.38) !important;
  }
}
.home-walkthrough-step-btn:focus-visible {
  outline: 2px solid rgba(56, 189, 248, 0.85);
  outline-offset: 2px;
}
/* Left rail: keep layout stable — no vertical nudge on hover */
.home-walkthrough-step-rail.home-walkthrough-step-btn:hover:not(.home-walkthrough-step-btn--active) {
  transform: none !important;
}
.home-walkthrough-step-btn:hover:not(.home-walkthrough-step-btn--active) {
  border-color: rgba(56, 189, 248, 0.38) !important;
  background: rgba(30, 41, 59, 0.62) !important;
  box-shadow: 0 8px 28px -12px rgba(0, 0, 0, 0.45) !important;
  transform: translateY(-1px);
}
.home-walkthrough-step-btn--active {
  transform: translateY(0);
}
.home-walkthrough-step-btn--active .home-walkthrough-step-chevron {
  animation: home-walkthrough-chevron-nudge 2.4s ease-in-out infinite;
}
@keyframes home-walkthrough-chevron-nudge {
  0%,
  100% {
    transform: translateX(0);
    opacity: 0.95;
  }
  50% {
    transform: translateX(3px);
    opacity: 1;
  }
}
.home-walkthrough-preview-glass {
  padding: 1px;
  border-radius: 21px;
  background: linear-gradient(
    145deg,
    rgba(56, 189, 248, 0.35) 0%,
    rgba(99, 102, 241, 0.12) 42%,
    rgba(15, 23, 42, 0.85) 100%
  );
  box-shadow:
    0 28px 72px -36px rgba(0, 0, 0, 0.65),
    0 0 56px -18px rgba(56, 189, 248, 0.18);
}
.home-walkthrough-live-dot {
  animation: home-walkthrough-live-pulse 2s ease-in-out infinite;
}
@keyframes home-walkthrough-live-pulse {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(34, 197, 94, 0.65);
    opacity: 1;
  }
  50% {
    box-shadow: 0 0 16px rgba(34, 197, 94, 0.95);
    opacity: 0.92;
  }
}
.home-walkthrough-timeline-rail {
  background: linear-gradient(90deg, rgba(56, 189, 248, 0.06) 0%, transparent 52%);
}
.home-walkthrough-step-btn--active .home-walkthrough-step-index {
  animation: home-walkthrough-step-pulse 2.2s ease-in-out infinite;
}
@keyframes home-walkthrough-step-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.45);
  }
  50% {
    box-shadow: 0 0 0 7px rgba(56, 189, 248, 0);
  }
}
.home-walkthrough-copy-fade {
  animation: home-walkthrough-copy-in 0.38s cubic-bezier(0.4, 0, 0.2, 1) both;
}
@keyframes home-walkthrough-copy-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .home-walkthrough-preview-img {
    transition: none !important;
  }
  .home-walkthrough-step-btn--active .home-walkthrough-step-index {
    animation: none !important;
  }
  .home-walkthrough-step-btn:hover:not(.home-walkthrough-step-btn--active) {
    transform: none !important;
  }
  .home-walkthrough-step-rail.home-walkthrough-step-btn:hover:not(.home-walkthrough-step-btn--active) {
    transform: none !important;
  }
  .home-walkthrough-step-btn--active .home-walkthrough-step-chevron {
    animation: none !important;
  }
  .home-walkthrough-live-dot {
    animation: none !important;
  }
  .home-walkthrough-copy-fade {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}
.home-modern-hero-bg {
  position: relative;
  /* visible so backdrop blur / glow layers are not clipped to a sharp rectangle */
  overflow: visible;
  background:
    radial-gradient(ellipse 88% 72% at 82% 38%, rgba(99, 102, 241, 0.22), transparent 54%),
    radial-gradient(ellipse 72% 58% at 8% 18%, rgba(56, 189, 248, 0.12), transparent 50%),
    linear-gradient(180deg, #0B1220 0%, #0E1A2B 46%, #0f172a 100%);
}
.home-modern-hero-bg::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.055) 0%, rgba(255, 255, 255, 0.02) 72px, transparent 160px),
    radial-gradient(ellipse 90% 50% at 50% -8%, rgba(56, 189, 248, 0.18), transparent 58%),
    radial-gradient(ellipse 42% 38% at 0% 72%, rgba(129, 140, 248, 0.1), transparent 50%),
    radial-gradient(ellipse 40% 36% at 100% 58%, rgba(14, 165, 233, 0.1), transparent 50%);
}
.home-hero-mesh {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.4;
  background-image:
    linear-gradient(rgba(148, 163, 184, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.055) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(ellipse 88% 75% at 50% 42%, black 0%, transparent 74%);
  -webkit-mask-image: radial-gradient(ellipse 88% 75% at 50% 42%, black 0%, transparent 74%);
}
.home-modern-grain {
  position: absolute;
  inset: 0;
  opacity: 0.032;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
.home-hero-visual-wrap {
  position: relative;
  z-index: 1;
  overflow: visible;
}
/* Clip oversized glow; mask feathers left/right so overflow:hidden is not a visible hard vertical line */
.home-hero-visual-glow-clip {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 36px;
  -webkit-mask-image: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.65) 10%,
    #000 20%,
    #000 80%,
    rgba(0, 0, 0, 0.65) 90%,
    rgba(0, 0, 0, 0) 100%
  );
  mask-image: linear-gradient(
    90deg,
    transparent 0%,
    black 16%,
    black 84%,
    transparent 100%
  );
}
.home-hero-visual-wrap > .home-hero-flow-root {
  position: relative;
  z-index: 1;
}
.home-hero-visual-glow {
  position: absolute;
  pointer-events: none;
  /* Oversized so any clip happens in fully transparent feather */
  width: 160%;
  height: 130%;
  left: -30%;
  top: -18%;
  z-index: 0;
  border-radius: 50%;
  background:
    radial-gradient(ellipse 52% 48% at 52% 52%, rgba(56, 189, 248, 0.32), transparent 62%),
    radial-gradient(ellipse 42% 40% at 78% 28%, rgba(129, 140, 248, 0.22), transparent 58%);
  filter: blur(36px);
  opacity: 0.85;
  mask-image: radial-gradient(ellipse 76% 64% at 50% 48%, black 0%, black 36%, transparent 84%);
  -webkit-mask-image: radial-gradient(ellipse 76% 64% at 50% 48%, black 0%, black 36%, transparent 84%);
}
@media (prefers-reduced-motion: reduce) {
  .home-hero-visual-glow {
    filter: blur(28px);
    opacity: 0.72;
  }
}
.home-hero-h1--dark {
  color: #f8fafc !important;
}
.home-modern-hero-bg .home-hero-h1 > .home-hero-h1-line:not(.home-hero-gradient-accent) {
  color: #f8fafc;
}
.home-hero-gradient-accent {
  background: linear-gradient(105deg, #38bdf8 0%, #22d3ee 38%, #a5f3fc 88%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.home-modern-hero-bg .home-hero-h1 {
  font-family: 'Inter', 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
  font-weight: 700;
  letter-spacing: -0.045em;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.04em;
  line-height: 1.04;
}
@media (max-width: 959px) {
  .home-hero-visual-glow {
    width: 118%;
    height: 120%;
    left: -9%;
    top: -12%;
  }
  .home-modern-hero-bg .home-hero-h1 {
    gap: 0.16em;
    line-height: 1.14;
    align-items: center;
  }
  .home-hero-lead-stack {
    gap: 10px;
  }
  .home-hero-cta-primary-glow {
    box-shadow:
      0 6px 22px rgba(249, 115, 22, 0.36),
      0 0 28px rgba(251, 146, 60, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.28) !important;
  }
  .home-modern-hero-bg .home-hero-cta-primary-glow:hover {
    box-shadow:
      0 10px 28px rgba(249, 115, 22, 0.42),
      0 0 36px rgba(251, 146, 60, 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.32) !important;
  }
  .home-hero-cta-row--stack {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  .home-hero-cta-row--stack > a {
    width: 100%;
    max-width: 100%;
  }
  .home-hero-cta-row--stack > a > span {
    width: 100%;
    justify-content: center;
    box-sizing: border-box;
  }
}
.home-modern-hero-bg .home-hero-h1 .home-hero-h1-line {
  display: block;
}
.home-hero-lead-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 520px;
}
.home-hero-lead-line {
  font-size: inherit;
  line-height: 1.55;
  color: #cbd5e1;
  margin: 0;
  font-weight: 500;
}
.home-modern-hero-bg .home-hero-eyebrow {
  display: inline-flex !important;
  align-items: center;
  gap: 10px;
  border-color: rgba(148, 163, 184, 0.32) !important;
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 4px 24px rgba(0, 0, 0, 0.2) !important;
  color: #cbd5e1 !important;
}
.home-hero-eyebrow-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7dd3fc, #38bdf8);
  box-shadow: 0 0 12px rgba(56, 189, 248, 0.85), 0 0 24px rgba(56, 189, 248, 0.35);
}
.home-modern-hero-bg .home-hero-trust-chip {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(148, 163, 184, 0.22) !important;
}
.home-modern-hero-bg .home-hero-trust-chip:hover {
  border-color: rgba(125, 211, 252, 0.4) !important;
  background: rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 4px 24px rgba(56, 189, 248, 0.12);
}
.home-modern-hero-bg .home-hero-trust-chip span {
  color: #cbd5e1 !important;
}
.home-hero-cta-primary-glow {
  background: linear-gradient(135deg, #fb923c 0%, #f97316 40%, #ea580c 100%) !important;
  color: #fff !important;
  box-shadow:
    0 8px 28px rgba(249, 115, 22, 0.42),
    0 0 48px rgba(251, 146, 60, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.28) !important;
  border: 1px solid rgba(253, 186, 116, 0.45);
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s ease !important;
}
.home-modern-hero-bg .home-hero-cta-primary-glow:hover {
  transform: scale(1.02) translateY(-1px) !important;
  box-shadow:
    0 14px 40px rgba(249, 115, 22, 0.5),
    0 0 56px rgba(251, 146, 60, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.32) !important;
}
@media (prefers-reduced-motion: reduce) {
  .home-hero-cta-primary-glow,
  .home-modern-hero-bg .home-hero-cta-primary-glow:hover {
    transition: none !important;
    transform: none !important;
  }
}
.home-modern-hero-bg .home-hero-cta-secondary {
  background: rgba(15, 23, 42, 0.35) !important;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(148, 163, 184, 0.32) !important;
  color: #f1f5f9 !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 4px 24px rgba(0, 0, 0, 0.2) !important;
  transition: transform 0.22s ease, border-color 0.22s ease, background 0.22s ease, box-shadow 0.22s ease !important;
}
.home-modern-hero-bg .home-hero-cta-secondary:hover {
  transform: scale(1.02) translateY(-1px) !important;
  border-color: rgba(125, 211, 252, 0.42) !important;
  background: rgba(30, 41, 59, 0.55) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.09), 0 8px 32px rgba(56, 189, 248, 0.12) !important;
}
@media (prefers-reduced-motion: reduce) {
  .home-modern-hero-bg .home-hero-cta-secondary:hover {
    transform: none !important;
  }
}
.home-hero-visual-slot {
  position: relative;
  z-index: 1;
}
@media (min-width: 960px) {
  .home-hero-visual-slot .home-hero-flow-root {
    transform: scale(1.04);
    transform-origin: center center;
  }
}
@media (min-width: 1480px) {
  .home-hero-visual-slot .home-hero-flow-root {
    transform: scale(1.06);
  }
}
@media (prefers-reduced-motion: reduce) {
  .home-hero-visual-slot .home-hero-flow-root {
    transform: none;
  }
}
@media (min-width: 960px) {
  .dc-home-main .home-hero-visual-slot .home-hero-flow-root {
    transform: scale(1.04);
    transform-origin: right center;
  }
}
@media (min-width: 1480px) {
  .dc-home-main .home-hero-visual-slot .home-hero-flow-root {
    transform: scale(1.06);
    transform-origin: right center;
  }
}
@media (prefers-reduced-motion: reduce) {
  .dc-home-main .home-hero-visual-slot .home-hero-flow-root {
    transform: none;
  }
}
.home-hero-h1 {
  font-family: 'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 700;
  letter-spacing: -0.045em;
  line-height: 1.06;
  color: #09090b;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.home-hero-eyebrow {
  font-family: 'Plus Jakarta Sans', 'Inter', system-ui, sans-serif;
  letter-spacing: 0.08em;
  border-color: rgba(228, 228, 231, 0.98) !important;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.9) !important;
}
.home-section-h2 {
  font-family: 'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 1.12;
  color: #18181b;
  -webkit-font-smoothing: antialiased;
}
.home-section-h2.home-section-h2--inverse {
  color: #f4f4f5;
}
.home-section-lead {
  color: #52525b;
}
.home-hero-trust-chip {
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.home-hero-trust-chip:hover {
  border-color: rgba(203, 213, 225, 0.95) !important;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: rgba(255, 255, 255, 0.92) !important;
}
.home-modern-cta-primary {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.home-modern-cta-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(248, 153, 37, 0.42) !important;
}
.home-modern-cta-secondary {
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}
.home-modern-cta-secondary:hover {
  transform: translateY(-2px);
  border-color: #cbd5e1 !important;
  background: #fafafa !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}
.home-solution-card {
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}
.home-solution-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 48px -18px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(86, 178, 227, 0.12);
  border-color: rgba(86, 178, 227, 0.35) !important;
}
.home-surface-dark .home-solution-card {
  border-color: rgba(71, 85, 105, 0.9) !important;
}
.home-surface-dark .home-solution-card:hover {
  border-color: rgba(125, 211, 252, 0.35) !important;
  box-shadow: 0 24px 48px -14px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(56, 189, 248, 0.12);
}
.home-problem-card {
  transition: border-color 0.2s ease, background 0.2s ease;
}
.home-problem-card:hover {
  border-color: rgba(125, 211, 252, 0.45);
  background: rgba(255, 255, 255, 0.09);
}
.home-step-ring {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.home-how-card:hover .home-step-ring--sky,
.home-how-card:hover .home-step-ring--orange {
  transform: scale(1.06);
}
.home-faq-paper.MuiAccordion-root:before { display: none; }
.home-faq-paper.MuiAccordion-root {
  border: 1px solid rgba(228, 228, 231, 0.95);
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04) !important;
}
.home-faq-paper.MuiAccordion-root.Mui-expanded {
  border-color: rgba(86, 178, 227, 0.35);
  box-shadow: 0 16px 40px -20px rgba(15, 23, 42, 0.12) !important;
}
.home-surface-dark .home-faq-paper.MuiAccordion-root {
  border-color: rgba(71, 85, 105, 0.95) !important;
  background: linear-gradient(165deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.99) 52%, rgba(12, 17, 28, 1) 100%) !important;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.05) !important;
}
.home-surface-dark .home-faq-paper.MuiAccordion-root.Mui-expanded {
  border-color: rgba(56, 189, 248, 0.35) !important;
  box-shadow: 0 16px 40px -16px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06) !important;
}
/* MUI AccordionSummary uses ButtonBase — default paper white shows through without this */
.home-surface-dark .home-faq-paper .MuiAccordionSummary-root.MuiButtonBase-root {
  background-color: transparent !important;
}
.home-surface-dark .home-faq-paper.Mui-expanded .MuiAccordionSummary-root.MuiButtonBase-root {
  background-color: rgba(15, 23, 42, 0.35) !important;
}
.home-surface-dark .home-faq-paper.Mui-expanded .MuiAccordionDetails-root {
  border-top: 1px solid rgba(51, 65, 85, 0.65);
}
.home-automate-section {
  position: relative;
  overflow: hidden;
}
.home-automate-section::before {
  content: '';
  position: absolute;
  inset: -20% -10% auto -10%;
  height: 70%;
  pointer-events: none;
  background: radial-gradient(ellipse 55% 45% at 50% 0%, rgba(86, 178, 227, 0.12), transparent 62%),
              radial-gradient(ellipse 40% 35% at 90% 30%, rgba(248, 153, 37, 0.08), transparent 55%);
}
.home-automate-section.home-surface-dark::before {
  /* Glow sits below the fold line so it does not read as a seam under the hero */
  background: radial-gradient(ellipse 70% 55% at 50% 18%, rgba(56, 189, 248, 0.07), transparent 58%),
              radial-gradient(ellipse 50% 45% at 85% 28%, rgba(129, 140, 248, 0.08), transparent 50%);
}
.home-surface-dark .home-section-h2 {
  color: #f8fafc !important;
}
.home-surface-dark .home-section-lead {
  color: #94a3b8 !important;
}
.home-automate-card {
  position: relative;
  overflow: hidden;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
  border: 1px solid rgba(226, 232, 240, 0.98) !important;
}
.home-surface-dark .home-automate-card {
  border: 1px solid rgba(71, 85, 105, 0.85) !important;
}
.home-automate-card::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  opacity: 0.95;
}
.home-automate-card--sky::before {
  background: linear-gradient(90deg, #0284c7, #56B2E3, #38bdf8);
}
.home-automate-card--orange::before {
  background: linear-gradient(90deg, #ea580c, #f89925, #fb923c);
}
.home-automate-card:hover {
  transform: translateY(-6px);
  border-color: rgba(86, 178, 227, 0.45) !important;
  box-shadow: 0 22px 48px -16px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(86, 178, 227, 0.1);
}
.home-surface-dark .home-automate-card:hover {
  border-color: rgba(125, 211, 252, 0.35) !important;
  box-shadow: 0 22px 48px -12px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(56, 189, 248, 0.15);
}
.home-automate-card:hover .home-automate-card-arrow {
  opacity: 1;
  transform: translateX(4px);
}
.home-automate-card .home-automate-card-cta {
  transition: color 0.2s ease, transform 0.2s ease;
}
.home-automate-card:hover .home-automate-card-cta {
  color: #bae6fd;
  transform: translateX(3px);
}
.home-automate-card-icon {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.home-automate-card:hover .home-automate-card-icon {
  transform: scale(1.06);
}
.home-solution-paper {
  position: relative;
  overflow: hidden;
}
.home-solution-paper::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  z-index: 1;
}
.home-solution-paper--sky::before {
  background: linear-gradient(90deg, #0284c7, #56B2E3);
}
.home-solution-paper--orange::before {
  background: linear-gradient(90deg, #ea580c, #f89925);
}
@media (max-width: 959px) {
  .home-solution-paper {
    overflow: hidden;
  }
  /* Full-width top tint reads as a “highlighted” card top, not a hairline */
  .home-solution-paper.home-solution-paper--mobileAccent::before {
    display: none;
  }
  .home-solution-paper.home-solution-paper--mobileAccent.home-solution-paper--sky {
    background:
      linear-gradient(180deg, rgba(125, 211, 252, 0.22) 0%, rgba(56, 189, 248, 0.1) 34%, transparent 56%),
      linear-gradient(165deg, rgba(30, 41, 59, 0.92) 0%, rgba(15, 23, 42, 0.96) 55%, rgba(12, 17, 30, 0.98) 100%) !important;
    border-color: rgba(56, 189, 248, 0.32) !important;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.07),
      0 0 0 1px rgba(56, 189, 248, 0.12),
      0 10px 36px rgba(0, 0, 0, 0.38) !important;
  }
  .home-solution-paper.home-solution-paper--mobileAccent.home-solution-paper--orange {
    background:
      linear-gradient(180deg, rgba(253, 186, 116, 0.24) 0%, rgba(251, 146, 60, 0.11) 36%, transparent 56%),
      linear-gradient(165deg, rgba(30, 41, 59, 0.92) 0%, rgba(15, 23, 42, 0.96) 55%, rgba(12, 17, 30, 0.98) 100%) !important;
    border-color: rgba(251, 146, 60, 0.28) !important;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      0 0 0 1px rgba(251, 146, 60, 0.1),
      0 10px 36px rgba(0, 0, 0, 0.38) !important;
  }
  .home-solution-card:hover,
  .home-surface-dark .home-solution-card:hover {
    transform: none !important;
  }
  .home-section-h2.home-solutions-h2 {
    letter-spacing: -0.018em !important;
    word-spacing: 0.16em;
  }
}
.home-solution-card-icon {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.home-solution-card:hover .home-solution-card-icon {
  transform: scale(1.06);
}
.home-surface-dark .home-how-card {
  border-color: rgba(71, 85, 105, 0.9) !important;
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.78) 0%, rgba(15, 23, 42, 0.94) 55%) !important;
  box-shadow: 0 20px 48px -28px rgba(0, 0, 0, 0.5) !important;
  position: relative;
  z-index: 0;
  isolation: isolate;
}
.home-surface-dark .home-step-ring--sky {
  background: linear-gradient(145deg, rgba(14, 165, 233, 0.22) 0%, rgba(30, 41, 59, 0.95) 100%) !important;
  color: #7dd3fc !important;
  border-color: rgba(56, 189, 248, 0.35) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
}
.home-surface-dark .home-step-ring--orange {
  background: linear-gradient(145deg, rgba(254, 215, 170, 0.2) 0%, rgba(251, 146, 60, 0.14) 100%) !important;
  color: #fb923c !important;
  border-color: rgba(251, 146, 60, 0.35) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.32) !important;
}
.home-surface-dark .home-how-card:hover .home-step-ring--sky {
  box-shadow: 0 12px 28px rgba(56, 189, 248, 0.18) !important;
}
.home-surface-dark .home-how-card:hover .home-step-ring--orange {
  box-shadow: 0 12px 28px rgba(251, 146, 60, 0.22) !important;
}
.home-surface-dark .home-faq-paper .MuiAccordionSummary-root .MuiTypography-root {
  color: #f1f5f9 !important;
}
.home-surface-dark .home-faq-paper .MuiAccordionDetails-root .MuiTypography-root {
  color: #e2e8f0 !important;
}
.home-surface-dark .home-faq-paper .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root {
  color: #cbd5e1 !important;
  opacity: 0.95;
}
.home-surface-dark .home-faq-paper .MuiAccordionSummary-expandIconWrapper:hover .MuiSvgIcon-root {
  color: #f1f5f9 !important;
}
@media (max-width: 959px) {
  .home-faq-paper.home-faq-paper--mobile .MuiAccordionSummary-root {
    min-height: 0 !important;
    padding: 8px 10px 8px 14px !important;
    align-items: flex-start;
  }
  .home-faq-paper.home-faq-paper--mobile .MuiAccordionSummary-content {
    margin: 6px 0 !important;
  }
  .home-faq-paper.home-faq-paper--mobile .MuiAccordionSummary-expandIconWrapper {
    padding: 4px 0 0;
    margin-top: 0;
  }
  .home-faq-paper.home-faq-paper--mobile .MuiAccordionDetails-root {
    padding: 0 14px 14px !important;
  }
}

/* Scroll-driven section entrances */
.home-scroll-reveal {
  opacity: 0;
  transition: opacity 0.75s cubic-bezier(0.22, 1, 0.36, 1), transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}
.home-scroll-reveal--fade-up {
  transform: translate3d(0, 40px, 0);
}
.home-scroll-reveal--fade-up-sm {
  transform: translate3d(0, 22px, 0);
}
.home-scroll-reveal--fade-in {
  transform: translate3d(0, 0, 0);
}
.home-scroll-reveal--slide-left {
  transform: translate3d(36px, 0, 0);
}
.home-scroll-reveal--slide-right {
  transform: translate3d(-36px, 0, 0);
}
.home-scroll-reveal--zoom-in {
  transform: translate3d(0, 16px, 0) scale(0.97);
}
.home-scroll-reveal--visible.home-scroll-reveal--fade-up,
.home-scroll-reveal--visible.home-scroll-reveal--fade-up-sm,
.home-scroll-reveal--visible.home-scroll-reveal--slide-left,
.home-scroll-reveal--visible.home-scroll-reveal--slide-right {
  opacity: 1;
  transform: translate3d(0, 0, 0);
  will-change: auto;
}
.home-scroll-reveal--visible.home-scroll-reveal--fade-in {
  opacity: 1;
  will-change: auto;
}
.home-scroll-reveal--visible.home-scroll-reveal--zoom-in {
  opacity: 1;
  transform: translate3d(0, 0, 0) scale(1);
  will-change: auto;
}
.home-scroll-reveal--has-stagger .home-stagger-child {
  opacity: 0;
  transform: translate3d(0, 20px, 0);
  transition: opacity 0.58s cubic-bezier(0.22, 1, 0.36, 1), transform 0.58s cubic-bezier(0.22, 1, 0.36, 1);
}
.home-scroll-reveal--has-stagger.home-scroll-reveal--visible .home-stagger-child {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}
@media (prefers-reduced-motion: reduce) {
  .home-scroll-reveal,
  .home-scroll-reveal.home-scroll-reveal--visible {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
    will-change: auto !important;
  }
  .home-scroll-reveal--has-stagger .home-stagger-child,
  .home-scroll-reveal--has-stagger.home-scroll-reveal--visible .home-stagger-child {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}
`;

/** Orange primary CTA — match Shopify → QuickBooks landing in-page register actions */
const primaryRegisterCtaButton = {
    background: 'linear-gradient(135deg, #fb923c 0%, #f97316 42%, #ea580c 100%)',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    padding: '14px 26px',
    fontSize: '16px',
    borderRadius: '14px',
    boxShadow: '0 8px 28px rgba(249, 115, 22, 0.4), 0 0 40px rgba(251, 146, 60, 0.28)',
    maxWidth: '100%',
};

export default function HomeComponent() {
    const theme = useTheme();
    const lg = useMediaQuery('(min-width:1480px)');
    const md = useMediaQuery(theme.breakpoints.up('md'));
    const smUp = useMediaQuery(theme.breakpoints.up('sm'));
    const automateFiveCol = useMediaQuery('(min-width:1240px)');

    const { ip } = useAppContext();
    useScript(ip);

    const sectionPadding = { padding: lg ? '0 50px' : md ? '0 25px' : '0 20px' };
    const howSectionPadding =
        lg || md
            ? { padding: lg ? '0 50px' : '0 25px' }
            : {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 'max(28px, calc(12px + env(safe-area-inset-left, 0px)))',
                  paddingRight: 'max(28px, calc(12px + env(safe-area-inset-right, 0px)))',
              };
    const pricingSectionPadding =
        lg || md
            ? { padding: lg ? '0 50px' : '0 25px' }
            : {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 'max(28px, calc(12px + env(safe-area-inset-left, 0px)))',
                  paddingRight: 'max(28px, calc(12px + env(safe-area-inset-right, 0px)))',
              };
    const faqSectionPadding =
        lg || md
            ? { padding: lg ? '0 50px' : '0 25px' }
            : {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 'max(28px, calc(12px + env(safe-area-inset-left, 0px)))',
                  paddingRight: 'max(28px, calc(12px + env(safe-area-inset-right, 0px)))',
              };
    /** MUI Paper/Accordion uses palette.background.paper (#fff) — inline beats JSS */
    const faqAccordionRootStyle = {
        backgroundColor: 'rgba(15, 23, 42, 0.98)',
        backgroundImage:
            'linear-gradient(165deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.99) 50%, rgba(10, 15, 26, 1) 100%)',
        color: '#f1f5f9',
    };
    const maxW = '1200px';
    const pricing = {
        ...SHOPIFY_QB_LANDING.pricing,
        cards: SHOPIFY_QB_LANDING.pricing.cards.map((card, cardIndex) =>
            cardIndex === 0 ? { ...card, highlight: 'LinkedIn Ads → Google Sheets\n= $30/month' } : card,
        ),
        summary: PRICING_LANDING.homeExampleSummary,
    };

    const automationPickGroups = [
        {
            categoryTitle: 'Marketing Reports',
            cards: [
                {
                    title: 'Stop exporting ad data every week',
                    description: 'All your ad performance in one place—no more CSV exports or copy/paste',
                    accent: 'orange',
                    Icon: TrendingUpOutlined,
                },
                {
                    title: 'Stop rebuilding reports from scratch',
                    description: 'Your weekly report stays up to date—no more Friday scramble',
                    accent: 'orange',
                    Icon: AssessmentOutlined,
                },
            ],
        },
        {
            categoryTitle: 'Sales & Accounting',
            cards: [
                {
                    title: 'Stop retyping orders into QuickBooks',
                    description: 'Sales sync automatically—no more duplicate entry',
                    accent: 'sky',
                    Icon: StorefrontOutlined,
                },
                {
                    title: 'Stop copying orders into spreadsheets',
                    description: 'Orders show up automatically—no more manual updates',
                    accent: 'sky',
                    Icon: TableChartOutlined,
                },
            ],
        },
        {
            categoryTitle: 'Operations',
            categoryKicker: 'And everything else:',
            cards: [
                {
                    title: 'Stop entering the same customer twice',
                    description: 'Your systems stay in sync—no more mismatched data',
                    accent: 'sky',
                    Icon: DeviceHubOutlined,
                },
            ],
        },
    ];

    const howSteps = [
        {
            title: 'Connect the tools you already use',
            text: 'Log into Shopify, Facebook, QuickBooks—whatever you already use. No setup. No engineering.',
            Icon: SyncAltIcon,
            accent: 'sky',
        },
        {
            title: 'Start with smart defaults',
            text: 'We pick the right data—you can tweak it anytime.',
            Icon: TuneOutlined,
            accent: 'orange',
        },
        {
            title: 'Stays up to date—automatically',
            text: 'Updates automatically. No exports. No rebuilds.',
            Icon: AutorenewOutlined,
            accent: 'sky',
        },
    ];

    const howFlowStrip = [{ label: 'Your data' }, { label: 'Updates automatically' }, { label: 'Anywhere you need it' }];

    const faqItems = [
        {
            question: 'What does Data Connector actually do?',
            answer:
                'It breaks the weekly export → copy → retype loop. Sales and orders land where you need them—updated for you.',
        },
        {
            question: 'Do I need technical skills?',
            answer: 'No. If you use Shopify or QuickBooks, you can set this up. We walk you through it.',
        },
        {
            question: 'Can I still use Excel or Google Sheets?',
            answer:
                'Yes. We don’t replace your spreadsheet—we keep it up to date. Same workbook, fewer Monday exports.',
        },
        {
            question: 'How long does setup take?',
            answer:
                'Most people are syncing in under five minutes—about eight clicks. Unusual setups can take longer; we’ll let you know if that applies.',
        },
    ];

    const heroHeadlineStyle = {
        fontSize: lg ? '3.125rem' : md ? '2.625rem' : '1.875rem',
        margin: md ? '0 0 28px 0' : '0 auto 20px',
        textAlign: md ? 'left' : 'center',
        maxWidth: '640px',
    };

    return (
        <MaterialUITheme>
            <style dangerouslySetInnerHTML={{ __html: homeScopedCss }} />
            <HomeSEO />
            <main id="main-content" className="dc-home-main" role="main" aria-label="Data Connector homepage">
                {/* Hero */}
                <ScrollRevealSection
                    aria-labelledby="hero-heading"
                    role="banner"
                    className="home-modern-hero-bg"
                    revealVariant="fade-up-sm"
                    rootMargin="0px 0px 45% 0px"
                    threshold={0.01}
                    style={{
                        padding: lg ? '88px 0 120px' : md ? '64px 0 96px' : '28px 0 120px',
                    }}
                >
                    <Box className="home-modern-grain" aria-hidden />
                    <Box className="home-hero-mesh" aria-hidden />
                    <div className="home-container main-container" style={{ maxWidth: maxW, margin: 'auto', ...sectionPadding, position: 'relative', zIndex: 1 }}>
                        <Grid container spacing={md ? 6 : 4} alignItems="center" wrap="wrap">
                            <Grid item xs={12} md={6} style={{ paddingBottom: md ? undefined : 8, position: 'relative', zIndex: 2, minWidth: 0 }}>
                                <Box
                                    component="span"
                                    className="home-hero-eyebrow"
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        marginBottom: md ? '22px' : '14px',
                                        padding: '9px 16px 9px 14px',
                                        borderRadius: 999,
                                    }}
                                >
                                    <Box component="span" className="home-hero-eyebrow-dot" aria-hidden />
                                    {'Reporting automation & live updates'}
                                </Box>
                                <h1 id="hero-heading" className="home-hero-h1" style={heroHeadlineStyle}>
                                    <span className="home-hero-h1-line">Stop losing hours to manual reports</span>
                                    <span className="home-hero-h1-line home-hero-gradient-accent">and copy-paste</span>
                                </h1>
                                <Box
                                    className="home-hero-lead-stack"
                                    component="div"
                                    style={{
                                        fontSize: lg ? '18px' : '16px',
                                        margin: md ? '0 0 32px 0' : '0 0 26px 0',
                                        textAlign: 'left',
                                    }}
                                >
                                    <p
                                        className="home-hero-lead-line"
                                        style={{
                                            color: '#e2e8f0',
                                            margin: 0,
                                            maxWidth: '560px',
                                            lineHeight: 1.62,
                                        }}
                                    >
                                        Automate your reports and keep your data in sync.
                                    </p>
                                </Box>
                                <Box
                                    className={md ? 'home-hero-cta-row' : 'home-hero-cta-row home-hero-cta-row--stack'}
                                    style={{ display: 'flex', gap: '14px', marginBottom: md ? '30px' : '26px', flexWrap: 'wrap', alignItems: 'center' }}
                                >
                                    <NavLink to={preserveTrackingParams('/register-company')} style={{ textDecoration: 'none' }}>
                                        <Box
                                            component="span"
                                            className="home-modern-cta-primary home-hero-cta-primary-glow"
                                            style={{
                                                padding: '15px 26px',
                                                borderRadius: '16px',
                                                fontWeight: 600,
                                                fontSize: '16px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Get started free
                                            <ArrowForwardIcon style={{ fontSize: 20 }} />
                                        </Box>
                                    </NavLink>
                                    <NavLink to="/#how-it-works" style={{ textDecoration: 'none' }}>
                                        <Box
                                            component="span"
                                            className="home-modern-cta-secondary home-hero-cta-secondary"
                                            style={{
                                                padding: '15px 22px',
                                                borderRadius: '14px',
                                                fontWeight: 600,
                                                fontSize: '16px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <PlayArrowOutlined style={{ fontSize: 22, opacity: 0.95 }} />
                                            See how it works
                                        </Box>
                                    </NavLink>
                                </Box>
                                <Typography
                                    component="p"
                                    style={{
                                        fontSize: '14px',
                                        color: '#94a3b8',
                                        margin: md ? '0 0 18px 0' : '0 0 20px 0',
                                        lineHeight: 1.55,
                                        maxWidth: '520px',
                                        textAlign: 'left',
                                    }}
                                >
                                    Set up in under five minutes. No tech background required.
                                </Typography>
                                <Box style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: md ? 0 : 6 }}>
                                    {['7-day free trial', 'No credit card', 'Clear pricing'].map((feature, index) => (
                                        <Box
                                            key={index}
                                            className="home-hero-trust-chip"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 14px',
                                                borderRadius: 999,
                                            }}
                                        >
                                            <CheckCircleIcon style={{ fontSize: '18px', color: '#38bdf8' }} />
                                            <span style={{ fontSize: '14px', fontWeight: 500 }}>{feature}</span>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={6}
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                    minWidth: 0,
                                    paddingLeft: md ? 20 : 0,
                                    boxSizing: 'border-box',
                                }}
                            >
                                <Box className="home-hero-visual-wrap home-hero-visual-slot" style={{ padding: md ? '12px 0 0 0' : '16px 0 0', maxWidth: '100%' }}>
                                    <Box className="home-hero-visual-glow-clip" aria-hidden>
                                        <Box className="home-hero-visual-glow" aria-hidden />
                                    </Box>
                                    <HomeHeroFlowVisual />
                                </Box>
                            </Grid>
                        </Grid>
                    </div>
                </ScrollRevealSection>

                {/* How it works */}
                <ScrollRevealSection
                    id="how-it-works"
                    className="home-surface-dark"
                    revealVariant="fade-in"
                    stagger={md}
                    style={{
                        padding: lg ? '88px 0 96px' : md ? '64px 0 80px' : '56px 0 88px',
                        background: 'linear-gradient(180deg, #0c1426 0%, #0f172a 50%, #101827 100%)',
                        scrollMarginTop: '72px',
                        borderTop: '1px solid rgba(51, 65, 85, 0.6)',
                        borderBottom: '1px solid rgba(51, 65, 85, 0.6)',
                    }}
                >
                    <Box maxWidth={maxW} margin="0 auto" style={howSectionPadding}>
                        <Typography
                            component="p"
                            style={{
                                fontSize: 12,
                                fontWeight: 800,
                                letterSpacing: '0.22em',
                                textTransform: 'uppercase',
                                color: '#64748b',
                                textAlign: 'center',
                                marginBottom: md ? 12 : 10,
                            }}
                        >
                            How it works
                        </Typography>
                        <Typography
                            component="h2"
                            className="home-section-h2"
                            aria-label="Set up your automation in eight clicks"
                            style={{
                                fontSize: lg ? '52px' : md ? '42px' : '34px',
                                marginBottom: md ? '18px' : '14px',
                                textAlign: 'center',
                                letterSpacing: '-0.04em',
                                lineHeight: 1.08,
                            }}
                        >
                            8 clicks.
                        </Typography>
                        <Box style={{ textAlign: 'center', maxWidth: '580px', margin: md ? '0 auto 28px' : '0 auto 22px' }}>
                            <Typography
                                style={{
                                    fontSize: lg ? '16px' : '15px',
                                    color: '#94a3b8',
                                    lineHeight: 1.55,
                                    fontWeight: 500,
                                    marginBottom: '14px',
                                }}
                            >
                                Set up your automation—connect once and your data stays updated automatically.
                            </Typography>
                            <Typography
                                style={{
                                    fontSize: lg ? '15px' : '14px',
                                    color: '#cbd5e1',
                                    lineHeight: 1.55,
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                }}
                            >
                                Most setups take under 5 minutes.
                            </Typography>
                        </Box>
                        <Box
                            className="home-how-flow"
                            aria-hidden
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: md ? '12px 14px' : '10px 12px',
                                margin: md ? '0 auto 48px' : '0 auto 40px',
                                maxWidth: '920px',
                            }}
                        >
                            {howFlowStrip.map((node, flowIndex) => (
                                <React.Fragment key={node.label}>
                                    {flowIndex > 0 ? (
                                        <ArrowForwardIcon
                                            style={{
                                                fontSize: 22,
                                                color: 'rgba(71, 85, 105, 0.95)',
                                                flexShrink: 0,
                                                opacity: 0.85,
                                            }}
                                        />
                                    ) : null}
                                    <Box
                                        component="span"
                                        className={`home-how-flow-chip${flowIndex === 1 ? ' home-how-flow-chip--pulse' : ''}`}
                                        style={{
                                            padding: '11px 18px',
                                            borderRadius: 12,
                                            fontSize: md ? 14 : 13,
                                            fontWeight: 700,
                                            letterSpacing: '0.02em',
                                            color: flowIndex === 1 ? '#e0f2fe' : '#e2e8f0',
                                            border:
                                                flowIndex === 1
                                                    ? '1px solid rgba(56, 189, 248, 0.65)'
                                                    : '1px solid rgba(71, 85, 105, 0.85)',
                                            background:
                                                flowIndex === 1
                                                    ? 'linear-gradient(165deg, rgba(56, 189, 248, 0.2) 0%, rgba(14, 116, 144, 0.22) 55%, rgba(15, 23, 42, 0.65) 100%)'
                                                    : 'rgba(15, 23, 42, 0.55)',
                                            boxShadow:
                                                flowIndex === 1
                                                    ? 'inset 0 1px 0 rgba(255,255,255,0.12)'
                                                    : 'inset 0 1px 0 rgba(255,255,255,0.04)',
                                        }}
                                    >
                                        {node.label}
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                        <Grid container spacing={md ? 6 : 5}>
                            {howSteps.map((s, stepIndex) => {
                                const StepIcon = s.Icon;
                                const iconOrange = s.accent === 'orange';
                                const isPayoffStep = stepIndex === 2;
                                return (
                                    <Grid item xs={12} md={4} key={s.title}>
                                        <Box
                                            className={md ? 'home-how-card home-stagger-child' : 'home-how-card'}
                                            textAlign={{ xs: 'left', md: 'center' }}
                                            style={{
                                                transitionDelay: md ? `${stepIndex * 100}ms` : '0ms',
                                                height: md ? '100%' : 'auto',
                                                padding: md ? '34px 28px 32px' : '24px 22px 26px',
                                                borderRadius: md ? '22px' : '18px',
                                                border: isPayoffStep
                                                    ? '1px solid rgba(56, 189, 248, 0.38)'
                                                    : '1px solid rgba(71, 85, 105, 0.75)',
                                                background: md
                                                    ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.72) 0%, rgba(15, 23, 42, 0.92) 58%)'
                                                    : 'linear-gradient(180deg, rgba(30, 41, 59, 0.88) 0%, rgba(15, 23, 42, 0.96) 52%, rgba(12, 17, 28, 0.98) 100%)',
                                                boxShadow: isPayoffStep
                                                    ? md
                                                        ? '0 24px 56px -26px rgba(0, 0, 0, 0.48), 0 0 48px -16px rgba(56, 189, 248, 0.18)'
                                                        : 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 12px 36px rgba(0, 0, 0, 0.38), 0 0 36px -12px rgba(56, 189, 248, 0.16)'
                                                    : md
                                                      ? '0 20px 48px -28px rgba(0, 0, 0, 0.45)'
                                                      : 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 10px 32px rgba(0, 0, 0, 0.38)',
                                                position: 'relative',
                                            }}
                                        >
                                            <Box
                                                className={`home-step-ring home-step-ring--${iconOrange ? 'orange' : 'sky'}`}
                                                aria-hidden
                                                style={{
                                                    width: md ? 56 : 50,
                                                    height: md ? 56 : 50,
                                                    borderRadius: '16px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginBottom: md ? '20px' : '16px',
                                                    borderStyle: 'solid',
                                                    borderWidth: 1,
                                                }}
                                            >
                                                <StepIcon style={{ fontSize: md ? 28 : 26 }} />
                                            </Box>
                                            <Typography
                                                component="h3"
                                                style={{
                                                    fontSize: lg ? '19px' : '17px',
                                                    fontWeight: 700,
                                                    letterSpacing: '-0.02em',
                                                    color: '#f8fafc',
                                                    marginBottom: '12px',
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {s.title}
                                            </Typography>
                                            <Typography
                                                style={{
                                                    fontSize: '15px',
                                                    color: '#94a3b8',
                                                    lineHeight: 1.72,
                                                    maxWidth: md ? '320px' : 'none',
                                                    margin: md ? '0 auto' : 0,
                                                }}
                                            >
                                                {s.text}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                </ScrollRevealSection>

                <HomeInteractiveSetup />
                <HomeDashboardShowcase />

                {/* Persona routing */}
                <ScrollRevealSection
                    aria-labelledby="persona-heading"
                    className="home-surface-dark"
                    revealVariant="fade-up-sm"
                    style={{
                        padding: lg ? '48px 0 56px' : md ? '36px 0 44px' : '28px 0 36px',
                        background: '#0f172a',
                    }}
                >
                    <Box
                        maxWidth={maxW}
                        margin="0 auto"
                        className="home-persona-infographic"
                        style={{ ...sectionPadding, position: 'relative', zIndex: 1 }}
                    >
                        <Typography
                            component="h2"
                            id="persona-heading"
                            className="home-section-h2"
                            style={{
                                fontSize: lg ? '28px' : md ? '24px' : '20px',
                                marginBottom: md ? '10px' : '8px',
                                textAlign: 'center',
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            What best describes you?
                        </Typography>
                        <Typography component="p" className="home-persona-kicker">
                            Tailored to your setup
                        </Typography>
                        <Box className="home-persona-cards">
                            <NavLink
                                to="/solutions/marketing-agencies"
                                style={{
                                    textDecoration: 'none',
                                    height: '100%',
                                    color: 'inherit',
                                }}
                                aria-label="Agencies and client work: stop rebuilding client reports—reports auto-update so they’re always ready to share. See agency setup."
                            >
                                    <Paper
                                        component="div"
                                        elevation={0}
                                        className="home-automate-card home-automate-card--orange home-stagger-child"
                                        style={{
                                            height: '100%',
                                            minWidth: 0,
                                            width: '100%',
                                            padding: '20px 18px 30px',
                                            borderRadius: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            font: 'inherit',
                                            background:
                                                'linear-gradient(165deg, rgba(30,41,59,0.82) 0%, rgba(15,23,42,0.92) 55%, rgba(12,17,30,0.95) 100%)',
                                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0, 0, 0, 0.35)',
                                            position: 'relative',
                                        }}
                                    >
                                        <Box
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                                marginBottom: 12,
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                className="home-automate-card-icon"
                                                aria-hidden
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    background:
                                                        'linear-gradient(145deg, rgba(254, 215, 170, 0.2) 0%, rgba(251, 146, 60, 0.15) 100%)',
                                                    border: '1px solid rgba(71, 85, 105, 0.9)',
                                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                                                    color: '#fb923c',
                                                }}
                                            >
                                                <BusinessOutlined style={{ fontSize: 26 }} />
                                            </Box>
                                            <Typography
                                                component="span"
                                                style={{
                                                    fontSize: md ? '17px' : '16px',
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.02em',
                                                    color: '#f8fafc',
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                Agencies & client work
                                            </Typography>
                                        </Box>
                                        <Box className="home-persona-flow" aria-hidden>
                                            <Box component="span" className="home-persona-node home-persona-node--orange">
                                                Client reports
                                            </Box>
                                            <ArrowForwardIcon className="home-persona-flow-chevron" />
                                            <Box component="span" className="home-persona-node home-persona-node--orange">
                                                Auto-update
                                            </Box>
                                            <ArrowForwardIcon className="home-persona-flow-chevron" />
                                            <Box component="span" className="home-persona-node home-persona-node--orange">
                                                Always ready
                                            </Box>
                                        </Box>
                                        <Typography
                                            component="p"
                                            style={{
                                                fontSize: '13px',
                                                lineHeight: 1.5,
                                                color: '#94a3b8',
                                                margin: '8px 0 14px',
                                                fontWeight: 500,
                                                letterSpacing: '0.01em',
                                            }}
                                        >
                                            No more Friday report rebuilds
                                        </Typography>
                                        <Typography
                                            component="span"
                                            style={{
                                                fontSize: '15px',
                                                fontWeight: 600,
                                                color: '#7dd3fc',
                                                marginTop: 'auto',
                                            }}
                                        >
                                            See Agency Setup
                                        </Typography>
                                        <ArrowForwardIcon
                                            className="home-automate-card-arrow"
                                            style={{
                                                position: 'absolute',
                                                right: 18,
                                                bottom: 18,
                                                fontSize: 20,
                                                color: '#64748b',
                                                opacity: 0.55,
                                                transition: 'opacity 0.2s ease, transform 0.2s ease',
                                            }}
                                        />
                                    </Paper>
                            </NavLink>
                            <NavLink
                                to="/marketing-dashboard-automation"
                                style={{
                                    textDecoration: 'none',
                                    height: '100%',
                                    color: 'inherit',
                                }}
                                aria-label="Your own business: scattered data comes together in one place and stays updated—no more pulling reports just to get answers. See your setup."
                            >
                                    <Paper
                                        component="div"
                                        elevation={0}
                                        className="home-automate-card home-automate-card--sky home-stagger-child"
                                        style={{
                                            height: '100%',
                                            minWidth: 0,
                                            width: '100%',
                                            padding: '20px 18px 30px',
                                            borderRadius: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            font: 'inherit',
                                            background:
                                                'linear-gradient(165deg, rgba(30,41,59,0.82) 0%, rgba(15,23,42,0.92) 55%, rgba(12,17,30,0.95) 100%)',
                                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0, 0, 0, 0.35)',
                                            position: 'relative',
                                        }}
                                    >
                                        <Box
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                                marginBottom: 12,
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                className="home-automate-card-icon"
                                                aria-hidden
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    background:
                                                        'linear-gradient(145deg, rgba(125, 211, 252, 0.18) 0%, rgba(14, 165, 233, 0.1) 100%)',
                                                    border: '1px solid rgba(71, 85, 105, 0.9)',
                                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                                                    color: '#7dd3fc',
                                                }}
                                            >
                                                <GroupOutlined style={{ fontSize: 26 }} />
                                            </Box>
                                            <Typography
                                                component="span"
                                                style={{
                                                    fontSize: md ? '17px' : '16px',
                                                    fontWeight: 800,
                                                    letterSpacing: '-0.02em',
                                                    color: '#f8fafc',
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                Your own business
                                            </Typography>
                                        </Box>
                                        <Box className="home-persona-flow" aria-hidden>
                                            <Box component="span" className="home-persona-node home-persona-node--sky">
                                                Scattered data
                                            </Box>
                                            <ArrowForwardIcon className="home-persona-flow-chevron" />
                                            <Box component="span" className="home-persona-node home-persona-node--sky">
                                                One place
                                            </Box>
                                            <ArrowForwardIcon className="home-persona-flow-chevron" />
                                            <Box component="span" className="home-persona-node home-persona-node--sky">
                                                Always updated
                                            </Box>
                                        </Box>
                                        <Typography
                                            component="p"
                                            style={{
                                                fontSize: '13px',
                                                lineHeight: 1.5,
                                                color: '#94a3b8',
                                                margin: '8px 0 14px',
                                                fontWeight: 500,
                                                letterSpacing: '0.01em',
                                            }}
                                        >
                                            No more pulling reports to get answers
                                        </Typography>
                                        <Typography
                                            component="span"
                                            style={{
                                                fontSize: '15px',
                                                fontWeight: 600,
                                                color: '#7dd3fc',
                                                marginTop: 'auto',
                                            }}
                                        >
                                            See Your Setup
                                        </Typography>
                                        <ArrowForwardIcon
                                            className="home-automate-card-arrow"
                                            style={{
                                                position: 'absolute',
                                                right: 18,
                                                bottom: 18,
                                                fontSize: 20,
                                                color: '#64748b',
                                                opacity: 0.55,
                                                transition: 'opacity 0.2s ease, transform 0.2s ease',
                                            }}
                                        />
                                    </Paper>
                            </NavLink>
                        </Box>
                    </Box>
                </ScrollRevealSection>

                {/* Jobs to take off your plate */}
                <ScrollRevealSection
                    aria-labelledby="automate-pick-heading"
                    className="home-automate-section home-surface-dark"
                    revealVariant="fade-in"
                    stagger
                    style={{
                        padding: lg ? '80px 0 88px' : '56px 0 64px',
                        /* Top matches hero bottom (#0f172a) for a continuous surface */
                        background: 'linear-gradient(180deg, #0f172a 0%, #0f172a 32%, #101827 52%, #0c1426 100%)',
                    }}
                >
                    <Box maxWidth={maxW} margin="0 auto" style={{ ...sectionPadding, position: 'relative', zIndex: 1 }}>
                        <Typography
                            component="h2"
                            id="automate-pick-heading"
                            className="home-section-h2"
                            style={{
                                fontSize: lg ? '38px' : md ? '30px' : '24px',
                                marginBottom: '12px',
                                textAlign: 'center',
                            }}
                        >
                            Still doing this by hand?
                        </Typography>
                        <Typography
                            className="home-section-lead"
                            style={{
                                fontSize: lg ? '17px' : '15px',
                                textAlign: 'center',
                                maxWidth: '560px',
                                margin: '0 auto 14px',
                                lineHeight: 1.65,
                            }}
                        >
                            Same exports. Same spreadsheets. Every week. Pick one—we’ll take it off your plate.
                        </Typography>
                        <Typography
                            style={{
                                fontSize: lg ? '16px' : '15px',
                                color: '#cbd5e1',
                                textAlign: 'center',
                                maxWidth: '620px',
                                margin: '0 auto 24px',
                                lineHeight: 1.45,
                                fontWeight: 700,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            Most teams start with one of these—pick yours:
                        </Typography>
                        <Box style={{ width: '100%' }}>
                            {automationPickGroups.map((group, groupIndex) => {
                                const priorCount = automationPickGroups
                                    .slice(0, groupIndex)
                                    .reduce((acc, g) => acc + g.cards.length, 0);
                                const gridCols =
                                    group.cards.length === 1
                                        ? 'minmax(0, min(440px, 100%))'
                                        : automateFiveCol
                                          ? 'repeat(2, minmax(0, 1fr))'
                                          : md
                                            ? 'repeat(2, minmax(0, 1fr))'
                                            : smUp
                                              ? 'repeat(2, minmax(0, 1fr))'
                                              : 'minmax(0, 1fr)';
                                return (
                                    <Box
                                        key={group.categoryTitle}
                                        style={{
                                            marginBottom: groupIndex < automationPickGroups.length - 1 ? (md ? 36 : 28) : 0,
                                        }}
                                    >
                                        {group.categoryKicker ? (
                                            <Typography
                                                component="p"
                                                style={{
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.02em',
                                                    color: '#94a3b8',
                                                    textAlign: 'center',
                                                    marginBottom: '6px',
                                                }}
                                            >
                                                {group.categoryKicker}
                                            </Typography>
                                        ) : null}
                                        <Typography
                                            component="h3"
                                            style={{
                                                fontSize: lg ? '15px' : '14px',
                                                fontWeight: 800,
                                                letterSpacing: '-0.02em',
                                                textTransform: 'none',
                                                color: '#cbd5e1',
                                                marginBottom: '14px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {group.categoryTitle}
                                        </Typography>
                                        <Box
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: gridCols,
                                                gap: '20px',
                                                width: '100%',
                                                ...(group.cards.length === 1 ? { justifyContent: 'center' } : {}),
                                            }}
                                        >
                                            {group.cards.map((card, cardIndex) => {
                                                const CardIcon = card.Icon;
                                                const cardIndexGlobal = priorCount + cardIndex;
                                                return (
                                                    <Paper
                                                        key={card.title}
                                                        component="button"
                                                        type="button"
                                                        elevation={0}
                                                        className={`home-automate-card home-automate-card--${card.accent} home-stagger-child`}
                                                        style={{
                                                            transitionDelay: `${cardIndexGlobal * 72}ms`,
                                                            minWidth: 0,
                                                            width: '100%',
                                                            padding: '24px 22px 22px',
                                                            borderRadius: '20px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start',
                                                            textAlign: 'left',
                                                            cursor: 'pointer',
                                                            font: 'inherit',
                                                            background:
                                                                'linear-gradient(165deg, rgba(30,41,59,0.82) 0%, rgba(15,23,42,0.92) 55%, rgba(12,17,30,0.95) 100%)',
                                                            boxShadow:
                                                                'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0, 0, 0, 0.35)',
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Box
                                                            className="home-automate-card-icon"
                                                            aria-hidden
                                                            style={{
                                                                width: 48,
                                                                height: 48,
                                                                borderRadius: '14px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                marginBottom: '16px',
                                                                background:
                                                                    card.accent === 'orange'
                                                                        ? 'linear-gradient(145deg, rgba(254, 215, 170, 0.2) 0%, rgba(251, 146, 60, 0.15) 100%)'
                                                                        : 'linear-gradient(145deg, rgba(125, 211, 252, 0.18) 0%, rgba(14, 165, 233, 0.1) 100%)',
                                                                border: '1px solid rgba(71, 85, 105, 0.9)',
                                                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
                                                                color: card.accent === 'orange' ? '#fb923c' : '#7dd3fc',
                                                            }}
                                                        >
                                                            <CardIcon style={{ fontSize: 26 }} />
                                                        </Box>
                                                        <Typography
                                                            component="span"
                                                            style={{
                                                                fontSize: '17px',
                                                                fontWeight: 700,
                                                                letterSpacing: '-0.02em',
                                                                color: '#f8fafc',
                                                                marginBottom: '10px',
                                                                lineHeight: 1.3,
                                                            }}
                                                        >
                                                            {card.title}
                                                        </Typography>
                                                        <Typography
                                                            component="span"
                                                            style={{
                                                                fontSize: '14px',
                                                                color: '#94a3b8',
                                                                lineHeight: 1.62,
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            {card.description}
                                                        </Typography>
                                                        <Box
                                                            style={{
                                                                marginTop: 'auto',
                                                                paddingTop: 16,
                                                                width: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-start',
                                                            }}
                                                        >
                                                            <Typography
                                                                component="span"
                                                                className="home-automate-card-cta"
                                                                style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: 700,
                                                                    color: '#7dd3fc',
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                Set this up →
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </ScrollRevealSection>

                {/* Trust */}
                <ScrollRevealSection
                    className="home-surface-dark"
                    revealVariant="fade-up-sm"
                    style={{
                        padding: lg ? '56px 0' : '44px 0',
                        background: 'linear-gradient(180deg, #101827 0%, #0f172a 55%, #0c1426 100%)',
                    }}
                >
                    <Box maxWidth="880px" margin="0 auto" style={sectionPadding} textAlign="center">
                        <Paper
                            elevation={0}
                            style={{
                                padding: lg ? '36px 40px' : '28px 24px',
                                borderRadius: 20,
                                border: '1px solid rgba(71, 85, 105, 0.9)',
                                background:
                                    'linear-gradient(135deg, rgba(30,41,59,0.85) 0%, rgba(15,23,42,0.95) 50%, rgba(12,17,30,0.92) 100%)',
                                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 56px -28px rgba(0, 0, 0, 0.45)',
                            }}
                        >
                            <Typography
                                style={{
                                    fontSize: lg ? '22px' : '18px',
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em',
                                    color: '#f8fafc',
                                    lineHeight: 1.45,
                                    marginBottom: '10px',
                                }}
                            >
                                Set it once. Your reports update themselves—automatically.
                            </Typography>
                            <Typography style={{ fontSize: '15px', color: '#94a3b8', lineHeight: 1.65, maxWidth: '560px', margin: '0 auto' }}>
                                No exports. No copy/paste. No fixing broken spreadsheets.
                            </Typography>
                        </Paper>
                        <Box
                            style={{
                                marginTop: lg ? '14px' : '12px',
                                paddingTop: lg ? '16px' : '14px',
                                textAlign: 'center',
                            }}
                        >
                            <Box
                                aria-hidden
                                style={{
                                    height: 1,
                                    width: 'min(280px, 78%)',
                                    margin: '0 auto 14px',
                                    background:
                                        'linear-gradient(90deg, transparent 0%, rgba(148, 163, 184, 0.42) 50%, transparent 100%)',
                                }}
                            />
                            <NavLink
                                to={preserveTrackingParams('/register-company')}
                                className="home-trust-micro-cta"
                                style={{
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: lg ? '16px' : '15px',
                                    fontWeight: 600,
                                }}
                            >
                                Set up automatic reporting in minutes
                                <ArrowForwardIcon style={{ fontSize: 18 }} />
                            </NavLink>
                        </Box>
                    </Box>
                </ScrollRevealSection>

                {/* Pricing — aligned with /shopify-to-quickbooks (sky cards, usage + summary, register CTA) */}
                <ScrollRevealSection
                    className="home-surface-dark"
                    revealVariant="fade-in"
                    stagger={md}
                    style={{
                        position: 'relative',
                        zIndex: 2,
                        isolation: 'isolate',
                        padding: lg ? '72px 0' : md ? '56px 0' : '48px 0 112px',
                        backgroundColor: '#0f172a',
                        backgroundImage:
                            'radial-gradient(ellipse 80% 55% at 50% -10%, rgba(56, 189, 248, 0.12), transparent 55%), linear-gradient(180deg, #101827 0%, #0f172a 48%, #0c1426 100%)',
                    }}
                >
                    <Box maxWidth="920px" margin="0 auto" style={{ ...pricingSectionPadding, textAlign: 'center' }}>
                        <Typography
                            component="h2"
                            className="home-section-h2"
                            style={{
                                fontSize: lg ? '30px' : md ? '24px' : '21px',
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                marginBottom: '12px',
                            }}
                        >
                            {pricing.heading}
                        </Typography>
                        <Typography
                            className="home-section-lead"
                            style={{
                                fontSize: md ? '17px' : '16px',
                                marginBottom: md ? '28px' : '20px',
                                maxWidth: '560px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                lineHeight: 1.55,
                                ...(md ? {} : { padding: '0 4px' }),
                            }}
                        >
                            {pricing.lead}
                        </Typography>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            justifyContent="center"
                            alignItems={md ? 'stretch' : 'flex-start'}
                            style={{
                                gap: md ? '24px' : '16px',
                                marginBottom: 0,
                                paddingBottom: md ? '56px' : '64px',
                                overflow: 'visible',
                            }}
                        >
                            {pricing.cards.map((card, priceIndex) => {
                                const Icon = card.icon === 'sheet' ? GridOnIcon : SyncAltIcon;
                                const priceCardBg = md
                                    ? 'radial-gradient(120% 85% at 50% 0%, rgba(14, 165, 233, 0.18) 0%, rgba(15, 23, 42, 0.95) 52%, rgba(12, 17, 30, 0.98) 100%)'
                                    : [
                                          'linear-gradient(180deg, rgba(125, 211, 252, 0.16) 0%, rgba(56, 189, 248, 0.07) 32%, transparent 52%)',
                                          'radial-gradient(120% 85% at 50% 0%, rgba(14, 165, 233, 0.16) 0%, rgba(15, 23, 42, 0.96) 52%, rgba(12, 17, 30, 0.98) 100%)',
                                      ].join(', ');
                                return (
                                    <Box
                                        key={card.kicker}
                                        className={md ? 'home-stagger-child' : undefined}
                                        style={{
                                            transitionDelay: md ? `${priceIndex * 110}ms` : '0ms',
                                            flex: '1 1 100%',
                                            maxWidth: md ? 'calc((100% - 24px) / 2)' : '100%',
                                            minWidth: 0,
                                            overflow: 'visible',
                                            paddingBottom: md ? '12px' : '4px',
                                        }}
                                    >
                                        <Box
                                            style={{
                                                height: md ? '100%' : 'auto',
                                                borderRadius: md ? '22px' : '18px',
                                                boxShadow: md
                                                    ? '0 24px 52px -18px rgba(0, 0, 0, 0.55)'
                                                    : '0 10px 28px -8px rgba(0, 0, 0, 0.5)',
                                                overflow: 'visible',
                                            }}
                                        >
                                            <Paper
                                                elevation={0}
                                                style={{
                                                    height: md ? '100%' : 'auto',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    borderRadius: md ? '22px' : '18px',
                                                    border: '1px solid rgba(56, 189, 248, 0.35)',
                                                    backgroundColor: 'rgba(15, 23, 42, 0.92)',
                                                    backgroundImage: priceCardBg,
                                                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06)',
                                                    padding: lg ? '32px 28px 28px' : md ? '26px 22px 24px' : '16px 18px 16px',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {md ? (
                                                    <Box
                                                        aria-hidden
                                                        style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            right: 0,
                                                            top: 0,
                                                            height: 5,
                                                            background: 'linear-gradient(90deg, #0284c7 0%, #0ea5e9 45%, #38bdf8 100%)',
                                                        }}
                                                    />
                                                ) : null}
                                                <Box
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: md ? 56 : 44,
                                                        height: md ? 56 : 44,
                                                        borderRadius: '50%',
                                                        marginBottom: md ? '16px' : '8px',
                                                        marginTop: md ? '6px' : 0,
                                                        background:
                                                            'linear-gradient(145deg, rgba(14, 165, 233, 0.2) 0%, rgba(56, 189, 248, 0.08) 100%)',
                                                        boxShadow:
                                                            'inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 20px -10px rgba(0, 0, 0, 0.45)',
                                                    }}
                                                >
                                                    <Icon style={{ fontSize: md ? 28 : 22, color: '#7dd3fc' }} />
                                                </Box>
                                                <Typography
                                                    component="p"
                                                    style={{
                                                        fontSize: md ? '13px' : '12px',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.02em',
                                                        textTransform: 'none',
                                                        color: '#38bdf8',
                                                        margin: md ? '0 0 12px' : '0 0 6px',
                                                    }}
                                                >
                                                    {card.kicker}
                                                </Typography>
                                                <Typography
                                                    component="p"
                                                    style={{
                                                        fontSize: lg ? '96px' : md ? '64px' : 'clamp(34px, 8.5vw, 42px)',
                                                        fontWeight: 800,
                                                        letterSpacing: md ? '-0.045em' : '-0.02em',
                                                        lineHeight: md ? 1.02 : 1.12,
                                                        color: '#e0f2fe',
                                                        margin: md ? '0 0 12px' : '0 0 4px',
                                                        padding: 0,
                                                    }}
                                                >
                                                    {card.amount}
                                                </Typography>
                                                <Typography
                                                    style={{
                                                        fontSize: md ? '17px' : '14px',
                                                        fontWeight: 700,
                                                        color: '#cbd5e1',
                                                        lineHeight: 1.35,
                                                        margin: 0,
                                                        marginBottom: card.highlight ? (md ? '16px' : '8px') : md ? '14px' : '8px',
                                                    }}
                                                >
                                                    {card.line}
                                                </Typography>
                                                {card.highlight ? (
                                                    <Typography
                                                        style={{
                                                            fontSize: lg ? '21px' : md ? '19px' : '14px',
                                                            fontWeight: 700,
                                                            color: '#f8fafc',
                                                            lineHeight: 1.5,
                                                            maxWidth: '100%',
                                                            margin: '0 auto',
                                                            letterSpacing: md ? '-0.015em' : 'normal',
                                                            padding: md ? 0 : '0 2px',
                                                            whiteSpace: 'pre-line',
                                                            overflowWrap: 'break-word',
                                                        }}
                                                    >
                                                        {card.highlight}
                                                    </Typography>
                                                ) : null}
                                                {card.detail ? (
                                                    <Typography
                                                        style={{
                                                            fontSize: md ? '14px' : '13px',
                                                            fontWeight: 500,
                                                            color: '#94a3b8',
                                                            lineHeight: 1.55,
                                                            maxWidth: md ? '340px' : '100%',
                                                            margin: md ? '0 auto' : '8px auto 0',
                                                            padding: md ? 0 : '0 2px',
                                                            whiteSpace: 'pre-line',
                                                        }}
                                                    >
                                                        {card.detail}
                                                    </Typography>
                                                ) : null}
                                            </Paper>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box
                            style={{
                                marginBottom: '28px',
                                padding: md ? '0 16px' : '0 4px',
                                textAlign: 'center',
                                maxWidth: '100%',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                        >
                            <Typography
                                component="p"
                                style={{
                                    fontSize: lg ? '26px' : md ? '22px' : '17px',
                                    fontWeight: 800,
                                    color: '#f1f5f9',
                                    marginTop: 0,
                                    marginBottom: md ? '16px' : '12px',
                                    lineHeight: 1.35,
                                    letterSpacing: '-0.03em',
                                }}
                            >
                                {pricing.summary.headline}
                            </Typography>
                            {pricing.summary.lines.map((line) => (
                                <Typography
                                    key={line}
                                    component="p"
                                    style={{
                                        fontSize: lg ? '16px' : md ? '15px' : '14px',
                                        fontWeight: 500,
                                        color: '#94a3b8',
                                        marginTop: 0,
                                        marginBottom: '8px',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {line}
                                </Typography>
                            ))}
                            <Typography
                                component="p"
                                style={{
                                    fontSize: md ? '14px' : '13px',
                                    fontWeight: 500,
                                    color: '#64748b',
                                    marginTop: md ? '14px' : '10px',
                                    marginBottom: 0,
                                    lineHeight: 1.5,
                                }}
                            >
                                {pricing.summary.supporting}
                            </Typography>
                        </Box>

                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{
                                marginTop: '8px',
                                paddingTop: '12px',
                                paddingBottom: '8px',
                                gap: '14px',
                            }}
                        >
                            <NavLink
                                to={preserveTrackingParams('/register-company')}
                                style={{ textDecoration: 'none', display: 'inline-flex', justifyContent: 'center', maxWidth: '100%' }}
                            >
                                <Button
                                    variant="contained"
                                    style={{
                                        ...primaryRegisterCtaButton,
                                        ...(md
                                            ? {}
                                            : {
                                                  padding: '12px 18px',
                                                  fontSize: '14px',
                                                  borderRadius: '12px',
                                              }),
                                    }}
                                >
                                    {pricing.primaryCta}
                                </Button>
                            </NavLink>
                            <NavLink
                                to={pricing.ctaTo}
                                style={{ textDecoration: 'none', display: 'inline-flex', justifyContent: 'center', maxWidth: '100%' }}
                            >
                                <Button
                                    variant="outlined"
                                    style={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: '12px',
                                        padding: md ? '11px 26px' : '10px 18px',
                                        borderWidth: '2px',
                                        borderColor: 'rgba(56, 189, 248, 0.55)',
                                        color: '#7dd3fc',
                                        fontSize: md ? '15px' : '14px',
                                    }}
                                >
                                    {pricing.ctaSecondary}
                                </Button>
                            </NavLink>
                        </Box>
                    </Box>
                </ScrollRevealSection>

                {/* Final CTA */}
                <ScrollRevealSection
                    revealVariant="fade-up-sm"
                    style={{
                        padding: lg ? '88px 0 96px' : '64px 0 88px',
                        background:
                            'linear-gradient(135deg, #0f172a 0%, #1e293b 42%, #0c4a6e 78%, #0369a1 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        aria-hidden
                        style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0.35,
                            background:
                                'radial-gradient(ellipse 60% 80% at 20% 20%, rgba(248, 153, 37, 0.35), transparent 55%), radial-gradient(ellipse 50% 60% at 85% 70%, rgba(86, 178, 227, 0.45), transparent 55%)',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box maxWidth="720px" margin="0 auto" style={{ ...sectionPadding, position: 'relative', zIndex: 1 }} textAlign="center">
                        <Typography
                            style={{
                                fontSize: lg ? '38px' : '28px',
                                fontWeight: 800,
                                letterSpacing: '-0.035em',
                                color: '#f8fafc',
                                marginBottom: '14px',
                                lineHeight: 1.15,
                            }}
                        >
                            Reports stay current—without the weekly rebuild
                        </Typography>
                        <Typography style={{ fontSize: lg ? '18px' : '16px', color: 'rgba(226, 232, 240, 0.92)', marginBottom: '32px', lineHeight: 1.65 }}>
                            Connect once. Skip the export-and-paste loop your team knows too well.
                        </Typography>
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: md ? 'row' : 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '14px',
                                flexWrap: 'wrap',
                            }}
                        >
                            <NavLink to={preserveTrackingParams('/register-company')} style={{ textDecoration: 'none' }}>
                                <Box
                                    component="span"
                                    className="home-modern-cta-primary"
                                    style={{
                                        background: 'linear-gradient(135deg, #fb923c 0%, #f97316 42%, #ea580c 100%)',
                                        color: '#fff',
                                        padding: '16px 34px',
                                        borderRadius: '16px',
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        cursor: 'pointer',
                                        border: '1px solid rgba(253, 186, 116, 0.45)',
                                        boxShadow:
                                            '0 10px 32px rgba(249, 115, 22, 0.38), 0 0 48px rgba(251, 146, 60, 0.22), inset 0 1px 0 rgba(255,255,255,0.22)',
                                    }}
                                >
                                    Start saving hours this week
                                    <ArrowForwardIcon style={{ fontSize: 22 }} />
                                </Box>
                            </NavLink>
                            <NavLink to="/#how-it-works" style={{ textDecoration: 'none' }}>
                                <Box
                                    component="span"
                                    className="home-modern-cta-secondary home-hero-cta-secondary"
                                    style={{
                                        padding: '15px 22px',
                                        borderRadius: '14px',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        border: '1px solid rgba(56, 189, 248, 0.35)',
                                        color: '#e2e8f0',
                                        background: 'rgba(15, 23, 42, 0.45)',
                                    }}
                                >
                                    <PlayArrowOutlined style={{ fontSize: 22, opacity: 0.95 }} />
                                    See how it works
                                </Box>
                            </NavLink>
                        </Box>
                    </Box>
                </ScrollRevealSection>

                {/* FAQ */}
                <ScrollRevealSection
                    aria-labelledby="faq-heading"
                    className="home-surface-dark"
                    revealVariant={md ? 'slide-left' : 'fade-in'}
                    style={{
                        padding: lg ? '72px 0 110px' : md ? '48px 0 96px' : '56px 0 96px',
                        background: 'linear-gradient(180deg, #0c1426 0%, #0f172a 40%, #101827 100%)',
                    }}
                >
                    <Box maxWidth="800px" margin="0 auto" style={faqSectionPadding}>
                        <Typography
                            variant="h3"
                            align="center"
                            id="faq-heading"
                            className="home-section-h2"
                            style={{
                                marginBottom: '12px',
                                fontSize: lg ? '30px' : md ? '24px' : '21px',
                                padding: md ? 0 : '0 4px',
                            }}
                        >
                            Questions, plain and simple
                        </Typography>
                        <Typography
                            className="home-section-lead"
                            style={{
                                textAlign: 'center',
                                fontSize: md ? '15px' : '14px',
                                marginBottom: md ? '36px' : '28px',
                                lineHeight: 1.6,
                                padding: md ? 0 : '0 6px',
                            }}
                        >
                            Straight answers—no runaround.
                        </Typography>
                        {faqItems.map((item, index) => (
                            <Accordion
                                key={index}
                                className={`home-faq-paper${md ? '' : ' home-faq-paper--mobile'}`}
                                style={{
                                    ...faqAccordionRootStyle,
                                    marginBottom: md ? '14px' : '11px',
                                    borderRadius: md ? '16px' : '14px',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <ExpandMoreIcon
                                            fontSize={md ? 'default' : 'small'}
                                            style={{ color: '#cbd5e1', opacity: 0.95 }}
                                        />
                                    }
                                    style={{
                                        backgroundColor: 'transparent',
                                        ...(md ? { minHeight: 56, padding: '12px 20px' } : {}),
                                    }}
                                >
                                    <Box style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                        <Typography
                                            style={{
                                                fontWeight: 600,
                                                fontSize: md ? '16px' : '15px',
                                                letterSpacing: '-0.015em',
                                                color: '#f1f5f9',
                                            }}
                                        >
                                            {item.question}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails
                                    style={{
                                        padding: md ? '0 20px 18px' : '0 14px 14px',
                                        backgroundColor: 'rgba(15, 23, 42, 0.88)',
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        style={{
                                            lineHeight: 1.68,
                                            fontSize: md ? '15px' : '15px',
                                            textAlign: 'left',
                                            color: '#e2e8f0',
                                        }}
                                    >
                                        {item.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </ScrollRevealSection>
            </main>
        </MaterialUITheme>
    );
}
